import { UseControllerProps } from "react-hook-form";
import { TextInputProps } from "react-native";
import { tCountryCodes } from "src/util/comboBoxData";
import {
  kEmailRegex,
  // kIbanRegex
} from "src/util/constants";

type tHookFormInput = Pick<UseControllerProps, "name" | "rules"> &
  TextInputProps & { label: string; helperText?: string };

type tData = {
  [keyof in tCountryCodes]: {
    businessFields: tHookFormInput[];
    contactFields: tHookFormInput[];
    bankingFields: tHookFormInput[];
  };
};

const emailField: tHookFormInput = {
  label: "Company email *",
  name: "emailAddress",
  rules: {
    required: "Required",
    pattern: {
      value: kEmailRegex,
      message: "Invalid email",
    },
  },
  placeholder: "test@business.com",
};

const businessNameField: tHookFormInput = {
  label: "Legal business Name *",
  name: "businessName",
  rules: { required: "Required" },
  placeholder: "My Business",
  helperText: "Enter business name exactly as it appears on your tax/IRS forms to avoid payout disruptions.",
};

const businessDomainField: tHookFormInput = {
  label: "Business website *",
  name: "businessDomain",
  rules: { required: "Required" },
  placeholder: "www.yoursite.com",
};

const address1Field: tHookFormInput = {
  label: "Business Address *",
  name: "address1",
  rules: { required: "Required" },
  placeholder: "Business Address",
};

const address2Field: tHookFormInput = {
  label: "Address line 2",
  name: "address2",
  placeholder: "Address line 2",
};

const cityField: tHookFormInput = {
  label: "City *",
  name: "city",
  rules: { required: "Required" },
  placeholder: "City",
};

const stateField: tHookFormInput = {
  label: "State *",
  name: "state",
  rules: { required: "Required" },
  placeholder: "State",
};

const provinceField: tHookFormInput = {
  label: "Province *",
  name: "state",
  rules: { required: "Required" },
  placeholder: "State",
};

const zipCodeField: tHookFormInput = {
  label: "Zip Code *",
  name: "zipCode",
  rules: { required: "Required" },
  placeholder: "Zip Code",
};

const phoneField: tHookFormInput = {
  label: "Phone",
  name: "phone",
  placeholder: "Phone",
};

const postalCodeField: tHookFormInput = {
  label: "Postal Code *",
  name: "zipCode",
  rules: { required: "Required" },
  placeholder: "Postal Code",
};

export const brandSetupForms: tData = {
  US: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Employer Identification Number (EIN)",
        name: "ein",
        rules: { required: "Required" },
        placeholder: "12-3456789",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, stateField, zipCodeField, phoneField],
    bankingFields: [
      {
        label: "Routing number *",
        name: "routingNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{9}$/,
          //   message: "Invalid Routing number",
          // },
        },
        placeholder: "110000000",
      },
      {
        label: "Account number *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // TODO: check if it's possible to apply a rule for all US account numbers
          // pattern: {
          //   value: /^\d{12}$/,
          //   message: "Invalid Account number",
          // },
        },
        placeholder: "000123456789",
      },
    ],
  },
  GB: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Company Registration Number (CRN)",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{8}$/,
          //   message: "Invalid CRN Number",
          // },
        },
        placeholder: "12345678",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "Sort number *",
        name: "routingNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{6}$/,
          //   message: "Invalid Sort number",
          // },
        },
        placeholder: "108800",
      },
      {
        label: "Account number *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{8}$/,
          //   message: "Invalid Account number",
          // },
        },
        placeholder: "00012345",
      },
    ],
  },
  CA: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Business Number (BN)",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{9}$/,
          //   message: "Invalid BN Number",
          // },
        },
        placeholder: "123456789",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, provinceField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "Transit number *",
        name: "routingNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{5}$/,
          //   message: "Invalid Sort number",
          // },
        },
        placeholder: "12345",
      },
      {
        label: "Institution number *",
        name: "routingNumber2",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{3}$/,
          //   message: "Invalid Institution number",
          // },
        },
        placeholder: "000",
      },
      {
        label: "Account number *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{12}$/,
          //   message: "Invalid Account number",
          // },
        },
        placeholder: "000123456789",
      },
    ],
  },
  DE: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Commercial Registry Number",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^HRB\s?\d{4}$/,
          //   message: "Invalid Commercial Registry Number",
          // },
        },
        placeholder: "HRB 1234",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "IBAN *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: kIbanRegex,
          //   message: "Invalid IBAN",
          // },
        },
        placeholder: "DE55370400440532014000",
      },
    ],
  },
  FR: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "SIREN number",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{9}$/,
          //   message: "Invalid SIREN Number",
          // },
        },
        placeholder: "123456789",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "IBAN *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: kIbanRegex,
          //   message: "Invalid IBAN",
          // },
        },
        placeholder: "FR1420041010050500013M02606",
      },
    ],
  },
  ES: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Tax ID number (NIF)",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^A\d{8}$/,
          //   message: "Invalid NIF Number",
          // },
        },
        placeholder: "A12345678",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "IBAN *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: kIbanRegex,
          //   message: "Invalid IBAN",
          // },
        },
        placeholder: "ES0700120345030000067890",
      },
    ],
  },
  AU: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Australian business number (ABN) *",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{11}$/,
          //   message: "Invalid ABN Number",
          // },
        },
        placeholder: "00111222333",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "BSB *",
        name: "routingNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{6}$/,
          //   message: "Invalid BSB number",
          // },
        },
        placeholder: "110000",
      },
      {
        label: "Account number *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{9}$/,
          //   message: "Invalid Account number",
          // },
        },
        placeholder: "000123456",
      },
    ],
  },
  SE: {
    businessFields: [
      emailField,
      businessNameField,
      {
        label: "Organization number *",
        name: "ein",
        rules: {
          required: "Required",
          // pattern: {
          //   value: /^\d{6}-?\d{4}$/,
          //   message: "Invalid Organization number",
          // },
        },
        placeholder: "123456-7890",
      },
      businessDomainField,
    ],
    contactFields: [address1Field, address2Field, cityField, postalCodeField, phoneField],
    bankingFields: [
      {
        label: "IBAN *",
        name: "accountNumber",
        rules: {
          required: "Required",
          // pattern: {
          //   value: kIbanRegex,
          //   message: "Invalid IBAN",
          // },
        },
        placeholder: "SE3550000000054910000003",
      },
    ],
  },
};
